import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import {
  getAvailableProductGroups,
  getProductGroups,
  getProductProducts, // Check if this matches your action name
  getProducts,
  setSelectedProductGroupId,
} from '../redux/pos/posSlice';
import { useThemeColors } from '../hooks/useThemeColors';

const Categories = ({ darkMode }) => {
  const dispatch = useDispatch();
  const productGroups = useSelector(getAvailableProductGroups);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftStart = useRef(0);

  // Hook se central colors nikal rahe hain
  const { textColor, cardBg, borderColor, contentBg, isDark } =
    useThemeColors(darkMode);

  useEffect(() => {
    dispatch(getProductGroups({}));
  }, [dispatch]);

  // Button Scroll Logic
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      if (direction === 'left') {
        scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  const handleCategoryClick = (category, index) => {
    setActiveIndex(index);
    dispatch(setSelectedProductGroupId(category.id));
    dispatch(getProducts({ productGroupId: category.id }));
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeftStart.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeftStart.current - walk;
  };

  const handleMouseUpOrLeave = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    startX.current = e.touches[0].pageX - scrollRef.current.offsetLeft;
    scrollLeftStart.current = scrollRef.current.scrollLeft;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeftStart.current - walk;
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
  };

  return (
    <div
      className="d-flex align-items-center w-100 transition-all shadow-sm mb-3"
      style={{
        backgroundColor: contentBg, // Wrapper background same as Menu container
        borderRadius: '16px', // Rounded corners matching Menu items
        padding: '0 12px',
        border: `1px solid ${borderColor}`,
      }}
    >
      {/* Left Navigation Button */}
      <button
        className="nav-scroll-btn btn btn-link border-0 d-flex align-items-center justify-content-center p-2 rounded-circle shadow-none me-2"
        onClick={() => scroll('left')}
        style={{
          backgroundColor: isDark
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(0,0,0,0.05)',
          transition: '0.2s',
        }}
      >
        <FaChevronLeft size={16} color={textColor} />
      </button>

      {/* Categories Container */}
      <div
        className="category-tabs-container flex-grow-1"
        ref={scrollRef}
        style={{
          scrollbarWidth: 'none', // Hides scrollbar for clean look
          overflowX: 'auto',
          cursor: 'grab',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="d-flex flex-nowrap gap-2 py-3">
          {productGroups.map((category, index) => {
            const isActive = activeIndex === index;

            return (
              <div
                key={category.id || index}
                className="category-tab flex-shrink-0 px-4 py-2 rounded d-flex align-items-center justify-content-center user-select-none text-nowrap fw-bold"
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '0.85rem',
                  letterSpacing: '0.3px',
                  // Active vs Inactive Styling
                  backgroundColor: isActive ? '#e4002b' : cardBg,
                  color: isActive ? '#ffffff' : textColor,
                  border: `1px solid ${isActive ? '#e4002b' : borderColor}`,
                  boxShadow: isActive
                    ? '0 4px 10px rgba(228, 0, 43, 0.3)'
                    : 'none',
                }}
                onClick={() => handleCategoryClick(category, index)}
              >
                {category.name}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Navigation Button */}
      <button
        className="btn btn-link border-0 d-flex align-items-center justify-content-center p-2 rounded-circle shadow-none nav-scroll-btn ms-2"
        onClick={() => scroll('right')}
        style={{
          backgroundColor: isDark
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(0,0,0,0.05)',
          transition: '0.2s',
        }}
      >
        <FaChevronRight size={16} color={textColor} />
      </button>

      {/* CSS to hide scrollbar across browsers */}
      <style>{`
        .category-tabs-container::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Categories;
